import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@design-system/box';
import { convertToDarkerRgba, useTheme } from '@design-system/theme';
const drawAnimationName = 'animated-border-draw';
const StyledAnimatedBorderContainer = styled.div `
  position: relative;
  display: inline-block;
  height: 100%;
  width: 100%;
  z-index: 1;

  /* Animation that draws the border then fades out */
  @keyframes ${drawAnimationName} {
    0% {
      stroke-dasharray: 0, 1;
      opacity: 1;
    }
    70% {
      stroke-dasharray: 1, 0;
      opacity: 1;
    }
    100% {
      stroke-dasharray: 1, 0;
      opacity: 0;
    }
  }
`;
const StyledSvgAnimatedBorder = styled.svg `
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  overflow: visible;
`;
const StyledContent = styled(Box) `
  display: block;
  position: relative;
  z-index: 1;
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
  width: 100%;
  height: 100%;
`;
export const AnimatedBorder = ({ animationDuration = '1.8s', blurIntensity = 0, borderColor, borderWidth = 1, children, trigger = false, }) => {
    const theme = useTheme();
    const [isAnimating, setIsAnimating] = useState(false);
    const blurId = useMemo(() => `blur-${uuidv4()}`, []);
    const gradientId = useMemo(() => `gradient-${uuidv4()}`, []);
    const primaryColor = borderColor || theme.colors.primary;
    const darkerPrimaryColor = borderColor
        ? convertToDarkerRgba(borderColor)
        : theme.colors.primaryDarker;
    const borderRadius = theme.themeVariables.globalBorderRadius;
    useEffect(() => {
        if (trigger && !isAnimating) {
            setIsAnimating(true);
        }
    }, [isAnimating, trigger]);
    return (_jsxs(StyledAnimatedBorderContainer, { children: [_jsxs(StyledSvgAnimatedBorder, { children: [_jsxs("defs", { children: [_jsxs("linearGradient", { gradientUnits: "objectBoundingBox", id: gradientId, x1: "0%", x2: "100%", y1: "0%", y2: "0%", children: [_jsx("stop", { offset: "0%", stopColor: primaryColor, stopOpacity: "0" }), _jsx("stop", { offset: "15%", stopColor: primaryColor, stopOpacity: "0.8" }), _jsx("stop", { offset: "40%", stopColor: primaryColor, stopOpacity: "1" }), _jsx("stop", { offset: "70%", stopColor: darkerPrimaryColor, stopOpacity: "1" }), _jsx("stop", { offset: "100%", stopColor: darkerPrimaryColor, stopOpacity: "0.9" })] }), blurIntensity > 0 && (_jsx("filter", { id: blurId, children: _jsx("feGaussianBlur", { stdDeviation: blurIntensity }) }))] }), _jsx("rect", { fill: "none", height: `calc(100% - ${borderWidth}px)`, pathLength: "1", rx: borderRadius, stroke: primaryColor, strokeDasharray: "1, 0", strokeLinecap: "round", strokeOpacity: isAnimating ? '0.7' : '0', strokeWidth: borderWidth, style: {
                            animation: isAnimating
                                ? `${drawAnimationName} ${animationDuration} ease-out forwards`
                                : 'none',
                            willChange: 'opacity',
                            transition: 'stroke-opacity 0.1s ease-out',
                        }, width: `calc(100% - ${borderWidth}px)`, x: borderWidth / 2, y: borderWidth / 2 }), _jsx("rect", { fill: "none", height: `calc(100% - ${borderWidth}px)`, pathLength: "1", rx: borderRadius, stroke: `url(#${gradientId})`, strokeDasharray: "0, 1", strokeLinecap: "round", strokeOpacity: isAnimating ? '1' : '0', strokeWidth: borderWidth, style: {
                            animation: isAnimating
                                ? `${drawAnimationName} ${animationDuration} ease-out forwards`
                                : 'none',
                            willChange: 'stroke-dasharray, opacity',
                            transition: 'stroke-opacity 0.1s ease-out',
                        }, width: `calc(100% - ${borderWidth}px)`, x: borderWidth / 2, y: borderWidth / 2 }), blurIntensity > 0 && (_jsx("rect", { fill: "none", filter: `url(#${blurId})`, height: `calc(100% - ${borderWidth}px)`, pathLength: "1", rx: borderRadius, stroke: `url(#${gradientId})`, strokeDasharray: "0, 1", strokeLinecap: "round", strokeOpacity: isAnimating ? '0.8' : '0', strokeWidth: borderWidth + 1, style: {
                            animation: isAnimating
                                ? `${drawAnimationName} ${animationDuration} ease-out forwards`
                                : 'none',
                            willChange: 'stroke-dasharray, opacity',
                            transition: 'stroke-opacity 0.1s ease-out',
                        }, width: `calc(100% - ${borderWidth}px)`, x: borderWidth / 2, y: borderWidth / 2 }))] }), _jsx(StyledContent, { children: children })] }));
};
export default AnimatedBorder;
//# sourceMappingURL=AnimatedBorder.js.map